/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { Editor } from '@monaco-editor/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSWRConfig } from 'swr';

import { Story } from '@/commons/protocols';
import DisplayError from '@/components/atoms/Error';
import { Button } from '@/components/ui/button';
import { useGet, useMutate } from '@/net/commons';

const StoryList = ({ isEditing }: { isEditing?: boolean }) => {
    const [editorValue, setEditorValue] = useState<string>('{}');
    const [error, setError] = useState<any>(undefined);
    const { projectSlug, suiteSlug, storySlug } = useParams();
    const { data: story, isLoading: isStoryLoading } = useGet<Story | undefined>(
        storySlug ? `/bff/project/${projectSlug}/suite/${suiteSlug}/story/slug/${storySlug}` : null,
    );

    const router = useNavigate();

    useEffect(() => {
        if (story) {
            try {
                setEditorValue(JSON.stringify(story, null, 2));
            } catch (e) {
                setError(e);
            }
        }
    }, [story, isStoryLoading]);
    const { mutate } = useSWRConfig();
    const { trigger } = useMutate<Story>(
        () => {
            if (isEditing) {
                return `/bff/project/${projectSlug}/suite/${suiteSlug}/story/${storySlug}`;
            }
            return `/bff/project/${projectSlug}/suite/${suiteSlug}/story`;
        },
        {
            revalidate: true,
            method: isEditing ? 'PUT' : 'POST',
            onSuccess: (data: any, key: string) => {
                const keyToUpdate = `/bff/project/${projectSlug}/suite/${suiteSlug}/story/slug/${data?.slug}`;
                mutate(keyToUpdate, data);
            },
        },
    );

    const onSubmit = async () => {
        try {
            const res = await trigger(JSON.parse(editorValue) as Story);
            router(`/story/${projectSlug}/${suiteSlug}/${res?.slug}`);
        } catch (e) {
            setError(e);
        }
    };

    return (
        <div className="w-2xl flex flex-col gap-2">
            {error && <DisplayError error={error} />}
            {editorValue && (
                <Editor
                    height="75vh"
                    defaultLanguage="json"
                    value={editorValue}
                    onChange={(e) => {
                        e && setEditorValue(e);
                    }}
                />
            )}
            <div className="flex flex-row-reverse">
                <Button onClick={onSubmit}> {isEditing ? 'Save' : 'Create'}</Button>
            </div>
        </div>
    );
};

export default StoryList;
